import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { chatId } from '~/lib/persistence/useChatHistory';
import {
  apiGetStaticDeploymentByChatId,
  apiConnectStaticDomain,
  apiDisconnectStaticDomain,
  apiGetStaticDomainStatus,
  type StaticDeployment,
} from '~/lib/api/deployments';
import { classNames } from '~/utils/classNames';
import { usePlanAccess, handlePlanRequired, showPlans, blockAndShowPlans } from '~/lib/planGate';

type Step = 'overview' | 'connect' | 'verifying';

/**
 * Surface the API's user-facing message (NestJS puts it at `response.data.message`)
 * instead of axios's generic "Request failed with status code NNN". Falls back to
 * the axios message, then a caller-provided default.
 */
function extractApiError(err: any, fallback: string): string {
  // Log the raw error for debugging; never surface axios/backend technical text to users.
  console.error(err);

  const apiMessage = err?.response?.data?.message;

  if (Array.isArray(apiMessage)) {
    return apiMessage.join(', ');
  }

  return apiMessage || fallback;
}

// ─── Presentational primitives (styling only — no behaviour) ───

// Product-card base: raised surface, hairline border, inset highlight + soft drop
// shadow for elevation, and a hover that lifts the border toward `subtle`.
const CARD =
  'rounded-xl border border-hairline bg-raised transition-all duration-200 ' +
  'shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset,0_12px_28px_-22px_rgba(0,0,0,0.85)]';

const CAPTION = 'text-[10px] font-medium uppercase tracking-[0.14em] text-muted';

function PanelHeader({ title, onBack }: { title: string; onBack?: () => void }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-hairline">
      {onBack ? (
        <button onClick={onBack} className="ui-btn-icon -ml-1.5" aria-label="Back">
          <div className="i-ph:arrow-left text-base" />
        </button>
      ) : (
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
          <div className="i-ph:globe text-[13px] text-accent" />
        </div>
      )}
      <h3 className="text-[13px] font-medium tracking-tight text-primary">{title}</h3>
    </div>
  );
}

function IconBadge({ icon, tone = 'accent' }: { icon: string; tone?: 'accent' | 'warning' }) {
  const tones = {
    accent: 'bg-accent/10 text-accent ring-accent/15',
    warning: 'bg-warning/10 text-warning ring-warning/15',
  };
  return (
    <div
      className={classNames(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ring-1 ring-inset',
        tones[tone],
      )}
    >
      <div className={classNames(icon, 'text-base')} />
    </div>
  );
}

function StatusPill({ tone, label }: { tone: 'live' | 'verifying'; label: string }) {
  if (tone === 'verifying') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-warning/25 bg-warning/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.08em] text-warning">
        <span className="h-1.5 w-1.5 rounded-full bg-warning animate-pulse" />
        {label}
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-accent/25 bg-accent/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.08em] text-accent">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
      </span>
      {label}
    </span>
  );
}

function VisitButton({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg border border-subtle bg-surface px-3 py-1.5 text-[12px] font-medium text-primary transition-all duration-150 hover:border-prominent hover:bg-sunken active:scale-[0.98]"
    >
      <div className="i-ph:arrow-square-out text-[13px] text-accent" />
      Visit site
    </a>
  );
}

function DisconnectButton({
  onClick,
  disabled,
  label = 'Disconnect',
}: {
  onClick: () => void;
  disabled: boolean;
  label?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium text-muted transition-colors duration-150 hover:bg-danger/10 hover:text-danger disabled:pointer-events-none disabled:opacity-50"
    >
      {disabled ? (
        <div className="i-svg-spinners:90-ring-with-bg text-[13px]" />
      ) : (
        <div className="i-ph:trash text-[13px]" />
      )}
      {label}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="ui-banner-error items-start leading-relaxed">
      <div className="i-ph:warning-circle mt-px shrink-0 text-sm text-danger" />
      <span className="text-danger/90">{message}</span>
    </div>
  );
}

/**
 * Replaces the "buy" and "connect" entry cards for free users. The server refuses
 * both actions, so offering the plans dialog is the only useful next step.
 */
function PlanLockedCard() {
  return (
    <div className={classNames(CARD, 'p-4')}>
      <div className="flex items-start gap-3">
        <IconBadge icon="i-ph:lock-simple" tone="warning" />
        <div className="min-w-0">
          <h4 className="mb-1 text-[13px] font-medium text-primary">Custom domains need a paid plan</h4>
          <p className="text-[12px] leading-relaxed text-muted">
            Your site stays live on its <span className="text-secondary">.dunea.app</span> address. Upgrade to connect a
            domain you own, or buy one through Dunea with DNS and SSL set up for you.
          </p>
        </div>
      </div>
      <button onClick={() => showPlans('domains_panel')} className="ui-btn-accent mt-3.5 w-full justify-center">
        View plans
      </button>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button onClick={handleCopy} className="ui-btn-icon" title="Copy to clipboard">
      <div className={classNames(copied ? 'i-ph:check text-accent' : 'i-ph:copy text-muted', 'text-[13px]')} />
    </button>
  );
}

function DnsRecord({ type, name, value }: { type: string; name: string; value: string }) {
  return (
    <div className="overflow-hidden rounded-xl border border-hairline bg-sunken">
      <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-secondary">
          <span className="rounded bg-accent/10 px-1.5 py-0.5 font-geist-mono text-[10px] normal-case tracking-normal text-accent">
            {type}
          </span>
          Record
        </span>
        <CopyButton text={value} />
      </div>
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 px-3 py-2.5">
        <span className="text-[11px] text-muted">Name</span>
        <code className="truncate font-geist-mono text-[12px] text-primary">{name}</code>
        <span className="text-[11px] text-muted">Value</span>
        <code className="break-all font-geist-mono text-[12px] text-primary">{value}</code>
      </div>
    </div>
  );
}

export function DomainsPanel() {
  const navigate = useNavigate();
  const currentChatId = useStore(chatId);
  const { isFree, planUnknown } = usePlanAccess();
  const [deployment, setDeployment] = useState<StaticDeployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>('overview');
  const [domain, setDomain] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDeployment = useCallback(async () => {
    if (!currentChatId) {
      setLoading(false);
      return;
    }

    try {
      const dep = await apiGetStaticDeploymentByChatId(currentChatId);
      setDeployment(dep);

      // A stored customDomain isn't proof it's live — Render may still be verifying
      // DNS/TLS. Check once on load so a reload lands in "Verifying DNS…" rather than
      // falsely showing "Connected".
      if (dep?.customDomain) {
        try {
          const status = await apiGetStaticDomainStatus(dep.id);

          if (!status.verified) {
            setStep('verifying');
          }
        } catch {
          // Status check failed — leave the overview as-is.
        }
      }
    } catch {
      setDeployment(null);
    } finally {
      setLoading(false);
    }
  }, [currentChatId]);

  useEffect(() => {
    loadDeployment();
  }, [loadDeployment]);

  // Poll verification status while in the "verifying" step. The domain is already
  // registered; we're waiting for DNS propagation + TLS + the site to actually serve.
  // To avoid flipping to "Connected" the instant a single check passes, we require
  // REQUIRED_STREAK consecutive successes (~10s of stable serving) — so when the UI
  // says Connected, the website genuinely loads. Stops when confirmed (step flips to
  // overview → cleanup) or the panel unmounts. `cancelled` guards post-cleanup state.
  useEffect(() => {
    if (step !== 'verifying' || !deployment) {
      return undefined;
    }

    const POLL_INTERVAL_MS = 10000;
    const REQUIRED_STREAK = 2; // ~10s of stable, verified serving before "Connected"
    let cancelled = false;
    let streak = 0;

    const poll = async () => {
      try {
        const status = await apiGetStaticDomainStatus(deployment.id);

        if (cancelled) {
          return;
        }

        if (status.verified) {
          streak += 1;

          if (streak >= REQUIRED_STREAK) {
            setDeployment((prev) => (prev ? { ...prev, customDomain: status.customDomain } : prev));
            setStep('overview');
          }
        } else {
          streak = 0; // any failed check restarts the confirmation window
        }
      } catch {
        streak = 0; // transient error — require a fresh run of consecutive successes
      }
    };

    poll();

    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [step, deployment?.id]);

  const handleConnect = async () => {
    if (!deployment || !domain.trim()) {
      return;
    }

    // Paid-only. Unreachable from the overview (the entry card is swapped for the
    // locked one), but a plan can lapse while this step is open.
    if (isFree) {
      blockAndShowPlans('custom_domain');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const cleanDomain = domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/$/, '');
      const updated = await apiConnectStaticDomain(deployment.id, cleanDomain);
      setDeployment(updated);
      // The domain is registered but not verified yet — go to the verifying step
      // (which polls) instead of showing "Connected" prematurely.
      setStep('verifying');
      setDomain('');
    } catch (err: any) {
      // A gate refusal opens the plans dialog instead of leaving a dead-end error.
      if (handlePlanRequired(err)) {
        setStep('overview');
      } else {
        setError(extractApiError(err, 'Could not connect the domain.'));
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!deployment) {
      return;
    }

    setDisconnecting(true);
    setError(null);

    try {
      const updated = await apiDisconnectStaticDomain(deployment.id);
      setDeployment(updated);
      // Leaving the verifying step (if we were in it) stops polling via effect cleanup.
      setStep('overview');
    } catch (err: any) {
      setError(extractApiError(err, 'Could not disconnect the domain.'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader title="Domains" />
        <div className="flex flex-1 items-center justify-center">
          <div className="i-svg-spinners:90-ring-with-bg text-xl text-muted" />
        </div>
      </div>
    );
  }

  if (!deployment) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader title="Domains" />
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:rocket-launch text-2xl text-accent" />
          </div>
          <h4 className="mb-1.5 text-[14px] font-medium text-primary">Deploy to add a domain</h4>
          <p className="max-w-[240px] text-[12px] leading-relaxed text-muted">
            Publish your landing page first, then connect a custom domain in a couple of clicks.
          </p>
        </div>
      </div>
    );
  }

  // Connect domain step
  if (step === 'connect') {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader title="Connect Domain" onBack={() => setStep('overview')} />

        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          {/* Domain input */}
          <section className="space-y-2">
            <label htmlFor="domain-input" className={classNames('block', CAPTION)}>
              Your domain
            </label>
            <input
              id="domain-input"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="ui-input"
            />
            <p className="text-[11px] text-muted">Enter it without http:// or a trailing slash.</p>
          </section>

          {/* DNS Instructions */}
          {domain.trim() && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="i-ph:dns text-[14px] text-accent" />
                <h4 className={CAPTION}>DNS Configuration</h4>
              </div>
              <p className="text-[12px] leading-relaxed text-muted">
                Add this record at your DNS provider. Changes can take a few minutes — up to 48 hours — to propagate.
              </p>

              <DnsRecord type="CNAME" name={domain.startsWith('www.') ? 'www' : '@'} value="sites.dunea.app" />

              <div className="ui-banner-warning items-start leading-relaxed">
                <div className="i-ph:lightbulb mt-px shrink-0 text-sm text-warning" />
                <span>
                  <span className="font-medium text-warning">Root domain?</span> Some providers need an ALIAS or ANAME
                  record instead of CNAME.
                </span>
              </div>
            </section>
          )}

          {error && <ErrorBanner message={error} />}
        </div>

        {/* Connect button */}
        <div className="border-t border-hairline p-4">
          <button
            onClick={handleConnect}
            disabled={connecting || !domain.trim()}
            className={classNames(
              'ui-btn-accent w-full flex items-center justify-center gap-2',
              (connecting || !domain.trim()) && 'opacity-50 cursor-not-allowed',
            )}
          >
            {connecting ? (
              <>
                <div className="i-svg-spinners:90-ring-with-bg text-sm" />
                Saving...
              </>
            ) : (
              <>
                <div className="i-ph:link text-sm" />
                Connect Domain
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Verifying step — domain registered, polling Render until DNS/TLS is verified.
  if (step === 'verifying') {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader title="Domains" />

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className={classNames(CARD, 'overflow-hidden')}>
            <div className="flex items-start justify-between gap-3 p-4">
              <div className="flex min-w-0 items-center gap-3">
                <IconBadge icon="i-svg-spinners:90-ring-with-bg" tone="warning" />
                <div className="min-w-0">
                  <div className={CAPTION}>Custom Domain</div>
                  <div className="truncate text-[13px] font-medium text-primary">{deployment.customDomain}</div>
                </div>
              </div>
              <StatusPill tone="verifying" label="Verifying" />
            </div>
            <div className="border-t border-hairline bg-warning/[0.03] px-4 py-3">
              <p className="text-[12px] leading-relaxed text-muted">
                Waiting for DNS propagation and SSL. We'll mark this <span className="text-secondary">Connected</span>{' '}
                only once your site actually loads — this updates automatically.
              </p>
            </div>
          </div>

          {/* DNS record reminder so the user can fix records while we wait */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="i-ph:dns text-[14px] text-accent" />
              <h4 className={CAPTION}>Required DNS Record</h4>
            </div>
            <DnsRecord
              type="CNAME"
              name={deployment.customDomain?.startsWith('www.') ? 'www' : '@'}
              value="sites.dunea.app"
            />
          </section>

          <DisconnectButton onClick={handleDisconnect} disabled={disconnecting} label="Cancel & disconnect" />

          {error && <ErrorBanner message={error} />}
        </div>
      </div>
    );
  }

  // Overview step
  return (
    <div className="flex h-full flex-col">
      <PanelHeader title="Domains" />

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Dunea subdomain */}
        <div className={classNames(CARD, 'overflow-hidden hover:border-subtle')}>
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="flex min-w-0 items-center gap-3">
              <IconBadge icon="i-ph:globe-simple" />
              <div className="min-w-0">
                <div className={CAPTION}>Dunea Domain</div>
                <div className="truncate text-[13px] font-medium text-primary">{deployment.subdomain}.dunea.app</div>
              </div>
            </div>
            <StatusPill tone="live" label="Active" />
          </div>
          <div className="border-t border-hairline px-4 py-2.5">
            <VisitButton href={deployment.deployUrl} />
          </div>
        </div>

        {/* Ways to add a domain — paid plans only. The Dunea subdomain above and an
            already-connected domain below stay available on every plan; only
            acquiring or attaching a new one is gated. Loading is its own state so a
            paying user never flashes the locked card. */}
        {planUnknown ? (
          <div className={classNames(CARD, 'flex items-center justify-center px-4 py-8')}>
            <div className="i-svg-spinners:90-ring-with-bg text-base text-muted" />
          </div>
        ) : isFree ? (
          <PlanLockedCard />
        ) : (
          <>
            {/* Buy a domain with Dunea — entry to the full domains page (search, buy,
                manage). Available whether or not a custom domain is connected. */}
            <button
              onClick={() => navigate('/domains')}
              className="group relative flex w-full items-center gap-3 rounded-xl border border-accent/40 bg-accent/5 p-4 text-left transition-all duration-200 hover:border-accent hover:bg-accent/10"
            >
              <span className="absolute right-3 top-3 rounded-full bg-accent/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-accent">
                Recommended
              </span>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-accent/15 ring-1 ring-inset ring-accent/25 transition-transform duration-200 group-hover:scale-105">
                <div className="i-ph:shopping-bag-open text-lg text-accent" />
              </div>
              <div className="min-w-0 pr-16">
                <div className="text-[13px] font-medium text-primary">Buy a domain with Dunea</div>
                <div className="text-[12px] text-muted">Search, purchase &amp; auto-connect — no DNS setup</div>
              </div>
              <div className="i-ph:arrow-right ml-auto text-accent opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 touch:opacity-100" />
            </button>

            {!deployment.customDomain && (
              <button
                onClick={() => setStep('connect')}
                className="group flex w-full items-center gap-3 rounded-xl border border-dashed border-subtle bg-raised p-4 text-left transition-all duration-200 hover:border-accent/40 hover:bg-sunken"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-accent/10 ring-1 ring-inset ring-accent/15 transition-transform duration-200 group-hover:scale-105">
                  <div className="i-ph:plus text-lg text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-primary">Connect a custom domain</div>
                  <div className="text-[12px] text-muted">Use your own domain like example.com</div>
                </div>
                <div className="i-ph:arrow-right ml-auto text-muted opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 touch:opacity-100" />
              </button>
            )}
          </>
        )}

        {/* A domain connected while on a paid plan stays visible (and disconnectable)
            after a downgrade — we never hide a user's live domain. */}
        {deployment.customDomain && (
          <div className={classNames(CARD, 'overflow-hidden hover:border-subtle')}>
            <div className="flex items-start justify-between gap-3 p-4">
              <div className="flex min-w-0 items-center gap-3">
                <IconBadge icon="i-ph:link-simple" />
                <div className="min-w-0">
                  <div className={CAPTION}>Custom Domain</div>
                  <div className="truncate text-[13px] font-medium text-primary">{deployment.customDomain}</div>
                </div>
              </div>
              <StatusPill tone="live" label="Connected" />
            </div>
            <div className="flex items-center gap-2 border-t border-hairline px-4 py-2.5">
              <VisitButton href={`https://${deployment.customDomain}`} />
              <div className="flex-1" />
              <DisconnectButton onClick={handleDisconnect} disabled={disconnecting} />
            </div>
          </div>
        )}

        {error && <ErrorBanner message={error} />}

        {/* Help section */}
        <div className="rounded-xl border border-hairline bg-surface p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-info/10 text-info ring-1 ring-inset ring-info/15">
              <div className="i-ph:lifebuoy text-[15px]" />
            </div>
            <div>
              <h4 className="mb-1 text-[13px] font-medium text-primary">Need a hand?</h4>
              <p className="text-[12px] leading-relaxed text-muted">
                Custom domains need a DNS record. After connecting, add the CNAME at your provider (Namecheap, GoDaddy,
                Cloudflare, …) and we'll verify it automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
